import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';

/**
 * Action for the "forgottenPayment" pending-attention case: staff forgot to
 * collect payment on site, but the customer actually was there and used the
 * service. Allows sending a proper invoice email to the customer so they can
 * still pay (bank transfer via PDF or online via payment link).
 */
function ForgottenPaymentInvoiceButton({
  booking,
  onAfterSend,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { sendForgottenPaymentInvoice } = useInternalBooking();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (booking.status !== 'confirmed') return false;
    if (!booking.presentSince) return false;
    if (booking.fullyPaid) return false;
    return authCtx.checkRightAny('internalBooking:special:resolveForgotten');
  }, [authCtx, booking]);

  if (!canShow) return null;

  const alreadySent = Boolean(booking?.forgottenPaymentInvoiceId);
  const hasMail = Boolean(booking?.mail);

  const handleOpenConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || !hasMail) return;
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setIsConfirmOpen(false);
  };

  const handleConfirmSend = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || !hasMail) return;

    try {
      setIsSubmitting(true);
      await sendForgottenPaymentInvoice(booking._id);
      await onAfterSend?.();
      setIsConfirmOpen(false);
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.message ||
        'Versand der Rechnung fehlgeschlagen.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  let label = 'Rechnung per Mail senden';
  if (!hasMail) label = 'Keine E-Mail-Adresse';
  if (alreadySent) label = 'Rechnung bereits gesendet';

  const primaryDisabled = isSubmitting || !hasMail || alreadySent;

  const sendButton = (
    <Button
      size={size}
      variant={variant}
      color={alreadySent ? 'success' : 'warning'}
      fullWidth={fullWidth}
      disabled={primaryDisabled}
      onClick={handleOpenConfirm}
      data-ignore-card-click
      sx={{ whiteSpace: 'nowrap', ...sx }}
    >
      {label}
    </Button>
  );

  return (
    <>
      {!hasMail ? (
        <Tooltip title='Für diese Buchung ist keine E-Mail-Adresse hinterlegt.'>
          <span
            style={fullWidth ? { display: 'block', width: '100%' } : { display: 'inline-block' }}
          >
            {sendButton}
          </span>
        </Tooltip>
      ) : (
        sendButton
      )}

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm} fullWidth maxWidth='sm'>
        <DialogTitle>Rechnung wirklich an den Kunden senden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' paragraph>
            Nur verwenden, wenn der Kunde <strong>tatsächlich nicht bezahlt</strong> hat
            und es lediglich vergessen wurde, ihn abzukassieren. Der Kunde erhält eine
            offizielle Rechnung per E-Mail an <strong>{booking?.mail || ''}</strong>,
            inklusive PDF-Anhang und Online-Zahlungslink.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Prüfe vorher, ob die offene Zahlung nicht doch schon kassiert wurde –
            in diesem Fall bitte stattdessen "Als bezahlt markieren" wählen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmSend}
            disabled={primaryDisabled}
            color='warning'
            variant='contained'
          >
            Rechnung jetzt senden
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ForgottenPaymentInvoiceButton;
